import type { HTMLAttributes, KeyboardEvent, ReactNode } from "react";
import { CheckCircle2, ChevronLeft, ChevronRight, Clock, Hourglass, XCircle } from "lucide-react";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Kanban del estándar del panel, tomado del tablero de Tickets (`portal-tickets/tickets/
 * TicketsKanban.tsx`): columnas de ancho fijo con scroll horizontal, cabecera neutra con conteo,
 * columna contraída como pestaña vertical angosta y tarjeta con borde suave.
 *
 * Solo es la forma; qué va en cada columna, cuándo se contrae y qué abre una tarjeta lo decide
 * quien lo monta. Las columnas aceptan props de `div` (onDragOver, onDrop…) para tableros con
 * arrastre. Tickets todavía pinta su propia copia: migrarlo aquí queda pendiente.
 *
 * Prioridad (DESIGN §6): la tarjeta lleva `prioridad` y pinta una franja izquierda de su color;
 * `KanbanPrioridad` pone el punto con su nombre en el pie. Mismos colores que Tickets (alta rojo,
 * media ámbar, baja verde). Qué hace a una tarjeta "alta" lo decide cada tablero (Ofertas: días
 * esperando decisión; Tickets: su prioridad capturada).
 */

export type PrioridadKanban = "alta" | "media" | "baja" | "sin";

/** Franja izquierda de la prioridad; exportada para tarjetas propias (p. ej. Tickets, que arrastra). */
export const PRIORIDAD_FRANJA: Record<PrioridadKanban, string> = {
  alta: "border-l-[3px] border-l-destructive",
  media: "border-l-[3px] border-l-amber-500",
  baja: "border-l-[3px] border-l-emerald-500",
  sin: "",
};
const PRIORIDAD_PUNTO: Record<PrioridadKanban, string> = {
  alta: "bg-destructive",
  media: "bg-amber-500",
  baja: "bg-emerald-500",
  sin: "bg-muted-foreground/40",
};
export const NOMBRE_PRIORIDAD_KANBAN: Record<PrioridadKanban, string> = {
  alta: "Alta",
  media: "Media",
  baja: "Baja",
  sin: "Sin prioridad",
};

/**
 * Una línea de dato de la tarjeta: se corta con "…" y el texto completo sale en tooltip. Es la
 * pieza que hace que todas las tarjetas de un tablero midan lo mismo (nada de line-clamp ni de
 * bloques que aparecen en unas tarjetas y en otras no: un vacío se escribe, p. ej. "Sin agente").
 * Con `label`, etiqueta fija + valor ("Propietario: Jorge").
 */
export function KanbanLinea({ texto, label, className }: { texto: string; label?: string; className?: string }) {
  return (
    <div className={cn("flex min-w-0 gap-1", className)}>
      {label && <span className="shrink-0">{label}</span>}
      <InfoTooltip content={texto}>
        <span className="min-w-0 truncate">{texto}</span>
      </InfoTooltip>
    </div>
  );
}

/** Punto de prioridad con su nombre (y un detalle opcional, p. ej. "40 días esperando"). */
export function KanbanPrioridad({ prioridad, detalle, className }: {
  prioridad: PrioridadKanban;
  detalle?: string;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground", className)}>
      <span className={cn("size-2 shrink-0 rounded-full", PRIORIDAD_PUNTO[prioridad])} aria-hidden />
      <span className="truncate">
        <span className="font-medium text-foreground">{NOMBRE_PRIORIDAD_KANBAN[prioridad]}</span>
        {detalle && <> - {detalle}</>}
      </span>
    </span>
  );
}

/** Ancho de una columna abierta: un poco más angosta en móvil para que asome la siguiente. */
export const KANBAN_COLUMNA_CLS = "w-[280px] sm:w-[300px]";

/** Contenedor del tablero: fila de columnas con scroll horizontal. */
export function KanbanTablero({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex gap-3 overflow-x-auto pb-4", className)}>{children}</div>;
}

export interface KanbanColumnaProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  titulo: string;
  conteo: number;
  colapsada: boolean;
  onToggle: () => void;
  /** Texto cuando la columna no tiene tarjetas. */
  vacioTexto?: string;
  /** Resalta la columna (por ejemplo, mientras se arrastra una tarjeta encima). */
  resaltada?: boolean;
  /**
   * Color del estatus de la columna (franja arriba, ícono y conteo), para leer el tablero de un
   * vistazo: `pendiente` gris, `proceso` ámbar, `exito` verde, `peligro` rojo. Sin tono, neutra.
   */
  tono?: TonoColumna;
  /** Nota corta en la cabecera (p. ej. "No se puede regresar" mientras se arrastra). */
  aviso?: string;
  /** Segunda línea de la cabecera abierta (p. ej. "Monto en la etapa: $1,200,000.00"). */
  subtitulo?: ReactNode;
  children?: ReactNode;
}

export type TonoColumna = "pendiente" | "proceso" | "exito" | "peligro";

/** Franja, ícono y conteo de cada tono. Mismos verdes/rojos/ámbar que Tickets y `StatusPill`. */
const TONO_COLUMNA: Record<TonoColumna, { franja: string; icono: typeof Clock; texto: string; conteo: string }> = {
  pendiente: { franja: "border-t-muted-foreground/40", icono: Clock, texto: "text-muted-foreground", conteo: "bg-muted text-muted-foreground" },
  proceso: { franja: "border-t-amber-500", icono: Hourglass, texto: "text-amber-600", conteo: "bg-amber-50 text-amber-700" },
  exito: { franja: "border-t-emerald-500", icono: CheckCircle2, texto: "text-emerald-600", conteo: "bg-emerald-50 text-emerald-700" },
  peligro: { franja: "border-t-destructive", icono: XCircle, texto: "text-destructive", conteo: "bg-destructive/10 text-destructive" },
};

/** Chip de conteo de la cabecera (mismo que Tickets). */
const CONTEO_CLS = "rounded bg-secondary px-1.5 py-0.5 font-medium tabular-nums text-secondary-foreground";

/** Columna del tablero: abierta (cabecera + lista con scroll) o contraída (pestaña vertical). */
export function KanbanColumna({
  titulo,
  conteo,
  colapsada,
  onToggle,
  vacioTexto = "Sin elementos en esta etapa",
  resaltada,
  tono,
  aviso,
  subtitulo,
  children,
  className,
  ...rest
}: KanbanColumnaProps) {
  const t = tono ? TONO_COLUMNA[tono] : null;
  const Icono = t?.icono;
  if (colapsada) {
    return (
      <div
        {...rest}
        className={cn(
          "flex w-11 shrink-0 self-stretch flex-col items-center rounded-lg border bg-muted/30 transition-colors",
          t && cn("border-t-[3px]", t.franja),
          resaltada && "border-primary bg-accent/60",
          className,
        )}
      >
        <InfoTooltip content={`Mostrar ${titulo}`} side="right">
          <button
            type="button"
            onClick={onToggle}
            aria-label={`Mostrar ${titulo}`}
            className="flex min-h-[240px] flex-1 cursor-pointer flex-col items-center gap-2 py-2 text-muted-foreground transition-opacity hover:opacity-80"
          >
            <ChevronRight className="size-4 shrink-0" />
            {Icono && <Icono className={cn("size-3.5 shrink-0", t?.texto)} aria-hidden />}
            <span className="whitespace-nowrap text-xs font-semibold [writing-mode:vertical-lr]">{titulo}</span>
            <span className={cn(CONTEO_CLS, t?.conteo, "text-[10px]")}>{conteo}</span>
          </button>
        </InfoTooltip>
      </div>
    );
  }

  return (
    <div
      {...rest}
      className={cn(
        "flex shrink-0 flex-col rounded-lg border bg-muted/30 transition-colors",
        KANBAN_COLUMNA_CLS,
        t && cn("border-t-[3px]", t.franja),
        resaltada && "border-primary bg-accent/60",
        className,
      )}
    >
      <div className="rounded-t-lg border-b bg-card px-3 py-2.5">
        <div className="flex items-center gap-2">
          {Icono && <Icono className={cn("size-4 shrink-0", t?.texto)} aria-hidden />}
          <span className="truncate text-sm font-semibold">{titulo}</span>
          <span className={cn(CONTEO_CLS, t?.conteo, "text-xs")}>{conteo}</span>
          {aviso && <span className="truncate text-[11px] text-muted-foreground">{aviso}</span>}
          <InfoTooltip content="Contraer columna">
            <button
              type="button"
              onClick={onToggle}
              aria-label={`Contraer ${titulo}`}
              className="ml-auto text-muted-foreground opacity-70 transition-opacity hover:opacity-100"
            >
              <ChevronLeft className="size-3.5" />
            </button>
          </InfoTooltip>
        </div>
        {subtitulo && <p className="mt-0.5 truncate text-[11px] tabular-nums text-muted-foreground">{subtitulo}</p>}
      </div>

      <div className="flex max-h-[calc(100vh-330px)] flex-col gap-2 overflow-y-auto p-2">
        {conteo === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-muted-foreground">{vacioTexto}</p>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

/**
 * Tarjeta del tablero. Se abre con clic o con Enter/Espacio (es un `article` enfocable, no un
 * botón, para poder llevar botones o popovers dentro sin anidarlos).
 */
export function KanbanTarjeta({
  onAbrir,
  etiqueta,
  prioridad,
  children,
  className,
}: {
  onAbrir: () => void;
  /** Nombre accesible de la tarjeta ("Abrir oferta O-000123"). */
  etiqueta: string;
  /** Franja izquierda del color de la prioridad. Sin valor (o "sin"), sin franja. */
  prioridad?: PrioridadKanban;
  children: ReactNode;
  className?: string;
}) {
  const alTeclear = (e: KeyboardEvent<HTMLElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onAbrir();
    }
  };
  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={etiqueta}
      onClick={onAbrir}
      onKeyDown={alTeclear}
      className={cn(
        "cursor-pointer rounded-lg border bg-card p-3 shadow-sm transition hover:border-primary/30 hover:shadow-md",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        prioridad && PRIORIDAD_FRANJA[prioridad],
        className,
      )}
    >
      {children}
    </article>
  );
}

/** Esqueleto del tablero mientras carga: columnas del mismo ancho que las reales. */
export function KanbanCargando({ columnas = 4 }: { columnas?: number }) {
  return (
    <KanbanTablero className="overflow-hidden">
      {Array.from({ length: columnas }, (_, i) => (
        <div key={i} className={cn("flex shrink-0 flex-col rounded-lg border bg-muted/30", KANBAN_COLUMNA_CLS)} aria-busy="true" aria-label="Cargando">
          <div className="flex items-center gap-2 rounded-t-lg border-b bg-card px-3 py-2.5">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-7 rounded-full" />
          </div>
          <div className="flex flex-col gap-2 p-2">
            {[0, 1, 2].map((t) => (
              <Skeleton key={t} className="h-24 rounded-lg" />
            ))}
          </div>
        </div>
      ))}
    </KanbanTablero>
  );
}
